import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, authInputStyle, authInputFocus, authInputBlur, authLabelStyle, authButtonStyle } from '../components/AuthShell';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPassword } = useAuthContext();

  if (!token) {
    return (
      <>
        <SEO title="Invalid Link - RRD Planners" description="Invalid reset link" />
        <AuthShell>
          <div style={{ textAlign: 'center' }}>
            <AlertTriangle size={48} style={{ color: '#e57373', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Invalid Reset Link</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>This link is invalid or has expired.</p>
            <a href="/auth/forgot-password" style={{
              display: 'inline-block', padding: '12px 32px', background: '#FFD93D', color: '#1a1a1a',
              borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 15
            }}>
              Request New Link
            </a>
          </div>
        </AuthShell>
      </>
    );
  }

  if (isSuccess) {
    return (
      <>
        <SEO title="Password Reset - RRD Planners" description="Password reset successful" />
        <AuthShell>
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#28a745', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Password Reset!</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Your password has been updated. Sign in with your new password.</p>
            <a href="/auth/login" style={{
              display: 'inline-block', padding: '12px 32px', background: '#FFD93D', color: '#1a1a1a',
              borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 15
            }}>
              Go to Sign In
            </a>
          </div>
        </AuthShell>
      </>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);

    if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }

    setIsSubmitting(true);
    try {
      const result = await resetPassword({ token, password: formData.password });
      if (result.success) { setIsSuccess(true); } else { setError(result.error || 'Reset failed'); }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Reset Password - RRD Planners" description="Set your new password" />
      <AuthShell>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>Reset Password</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ background: 'rgba(229,115,115,0.1)', border: '1px solid #e57373', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#e57373', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} required value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 8 characters" autoComplete="new-password"
                style={{ ...authInputStyle, paddingRight: 44 }} onFocus={authInputFocus} onBlur={authInputBlur} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={authLabelStyle}>Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showConfirmPassword ? 'text' : 'password'} required value={formData.confirmPassword}
                onChange={e => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                placeholder="Confirm your password" autoComplete="new-password"
                style={{ ...authInputStyle, paddingRight: 44 }} onFocus={authInputFocus} onBlur={authInputBlur} />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={isSubmitting} style={authButtonStyle(isSubmitting)}>
            {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Resetting...</> : 'Reset Password'}
          </button>
        </form>

        <p style={{ color: '#666', fontSize: 14, marginTop: 24, textAlign: 'center' }}>
          <a href="/auth/login" style={{ color: '#FFD93D', fontWeight: 600, textDecoration: 'none' }}>Back to Sign In</a>
        </p>
      </AuthShell>
    </>
  );
};

export default ResetPasswordPage;
